/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import type { TFunction } from 'i18next';
import type { SaleState } from '../../constants/sale-states';
import type { Sale } from '../../types/sale.types';

/**
 * Sale-value brackets, in reais, offered as one-click ranges.
 *
 * Reais rather than cents: `accepted_value` is stored in reais, unlike the
 * lease's `amount_cents`, and converting here would only invite the two to
 * be read with the same unit.
 */
export const VALUE_BRACKETS = [
  { id: 'upTo300k', min: 0, max: 300000 },
  { id: 'from300kTo600k', min: 300000, max: 600000 },
  { id: 'from600kTo1m', min: 600000, max: 1000000 },
  { id: 'above1m', min: 1000000, max: Number.POSITIVE_INFINITY },
] as const;

export type ValueBracketId = (typeof VALUE_BRACKETS)[number]['id'];

/**
 * Paperwork is offered as complete/pending rather than milestone-by-milestone.
 *
 * The milestones land out of order and weeks apart, so "which one is missing"
 * is a detail question the row already answers with its `2/4`. What the
 * broker filters on is the state of the pile: done, or still open.
 */
export const PAPERWORK_STATUSES = ['complete', 'pending'] as const;

export type PaperworkStatusId = (typeof PAPERWORK_STATUSES)[number];

export interface SaleFilters {
  states?: SaleState[];
  brokers?: string[];
  valueBrackets?: ValueBracketId[];
  paperwork?: PaperworkStatusId[];
}

/**
 * Whether every milestone the agency requires for completeness has a date.
 *
 * A milestone configured with `required=false` (financing, by default: a
 * cash purchase has none to approve) is excluded — counting it would report
 * paperwork that will never complete.
 *
 * @param sale - The sale record.
 * @param requiredMilestoneIds - Ids of the agency's `required` milestone types.
 */
export function isPaperworkComplete(sale: Sale, requiredMilestoneIds: readonly string[]): boolean {
  return requiredMilestoneIds.every((id) => Boolean(sale.paperwork[id]));
}

/**
 * Whether the sale's accepted value falls in one of the selected brackets.
 */
function matchesValue(sale: Sale, brackets: readonly ValueBracketId[]): boolean {
  const value = sale.accepted_value;
  if (typeof value !== 'number') return false;
  return VALUE_BRACKETS.some(
    (b) => brackets.includes(b.id) && value >= b.min && value < b.max,
  );
}

/**
 * Whether the sale's paperwork completeness matches one of the statuses.
 */
function matchesPaperwork(
  sale: Sale, statuses: readonly PaperworkStatusId[], requiredMilestoneIds: readonly string[],
): boolean {
  return statuses.includes(isPaperworkComplete(sale, requiredMilestoneIds) ? 'complete' : 'pending');
}

/**
 * Whether the state filter (if active) accepts this sale.
 */
function passesStateFilter(sale: Sale, filters: SaleFilters): boolean {
  return !filters.states?.length || filters.states.includes(sale.state);
}

/**
 * Whether the broker filter (if active) accepts this sale.
 */
function passesBrokerFilter(sale: Sale, filters: SaleFilters): boolean {
  return !filters.brokers?.length
    || filters.brokers.includes(sale.responsible_broker_email ?? '');
}

/**
 * Whether the value-bracket filter (if active) accepts this sale.
 */
function passesValueFilter(sale: Sale, filters: SaleFilters): boolean {
  return !filters.valueBrackets?.length || matchesValue(sale, filters.valueBrackets);
}

/**
 * Whether the paperwork filter (if active) accepts this sale.
 */
function passesPaperworkFilter(
  sale: Sale, filters: SaleFilters, requiredMilestoneIds: readonly string[],
): boolean {
  return !filters.paperwork?.length
    || matchesPaperwork(sale, filters.paperwork, requiredMilestoneIds);
}

/**
 * Whether a sale clears every active filter section.
 */
function matchesSaleFilters(
  sale: Sale, filters: SaleFilters, requiredMilestoneIds: readonly string[],
): boolean {
  return passesStateFilter(sale, filters)
    && passesBrokerFilter(sale, filters)
    && passesValueFilter(sale, filters)
    && passesPaperworkFilter(sale, filters, requiredMilestoneIds);
}

/**
 * Apply *filters* to *sales*.
 *
 * Each section narrows (AND between sections), while choices inside one
 * section widen (OR) — the same behaviour as every other filter panel in the
 * app, so the same clicks mean the same thing here.
 *
 * @param requiredMilestoneIds - Ids of the agency's `required` milestone
 * types, needed to judge the `paperwork` filter section.
 */
export function applySaleFilters(
  sales: Sale[], filters: SaleFilters, requiredMilestoneIds: readonly string[],
): Sale[] {
  return sales.filter((sale) => matchesSaleFilters(sale, filters, requiredMilestoneIds));
}

/**
 * How many individual choices are active, across every section.
 */
export function countSaleFilters(filters: SaleFilters): number {
  return (filters.states?.length ?? 0)
    + (filters.brokers?.length ?? 0)
    + (filters.valueBrackets?.length ?? 0)
    + (filters.paperwork?.length ?? 0);
}

/**
 * One removable chip label per active choice.
 *
 * Labels are the chip's identity as well as its text — `PageHeader` hands the
 * clicked label straight back to `onRemoveFilter` — so they have to stay
 * unique across sections. The broker's own e-mail and the localized range
 * labels already are.
 */
export function buildSaleFilterLabels(
  filters: SaleFilters,
  t: TFunction,
  stateLabel: (state: string) => string,
): string[] {
  return [
    ...(filters.states ?? []).map(stateLabel),
    ...(filters.paperwork ?? []).map((id) => t(`filters.paperworkStatuses.${id}`)),
    ...(filters.valueBrackets ?? []).map((id) => t(`filters.valueBrackets.${id}`)),
    ...(filters.brokers ?? []),
  ];
}

/**
 * Drop whichever choice produced *label*.
 *
 * Compares against the same rendered labels `buildSaleFilterLabels` emits, so
 * a chip removes exactly the choice it displays.
 */
export function removeSaleFilterByLabel(
  filters: SaleFilters,
  label: string,
  t: TFunction,
  stateLabel: (state: string) => string,
): SaleFilters {
  return {
    states: (filters.states ?? []).filter((s) => stateLabel(s) !== label),
    brokers: (filters.brokers ?? []).filter((b) => b !== label),
    valueBrackets: (filters.valueBrackets ?? [])
      .filter((id) => t(`filters.valueBrackets.${id}`) !== label),
    paperwork: (filters.paperwork ?? [])
      .filter((id) => t(`filters.paperworkStatuses.${id}`) !== label),
  };
}

/**
 * The brokers who actually answer for a sale, sorted.
 *
 * Derived from the records rather than from the agency roster: a filter
 * offering members with no sales would guarantee empty results, and sales
 * whose broker has since left the agency would be unreachable.
 */
export function saleBrokerOptions(sales: Sale[]): string[] {
  const emails = new Set(
    sales.map((s) => s.responsible_broker_email).filter(Boolean) as string[],
  );
  return [...emails].sort((a, b) => a.localeCompare(b));
}
